import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { Loader2, Save, ShieldCheck } from "lucide-react";
import Button from "@/shared/components/button";
import Field from "@/shared/components/field";
import JsonSchemaEditor from "@/shared/components/json_schema_editor";
import DynamicJsonForm from "@/shared/components/DynamicJsonForm";
import { useIntegrationSetups } from "../hooks/useIntegrationSetups";
import { useIntegrationProviders } from "../../integrations_provider/hooks/useIntegrationProviders";
import type IntegrationSetupCreate from "../models/IntegrationSetupCreate";
import type IntegrationSetupDto from "../models/IntegrationSetup";
import type IntegrationProvider from "../../integrations_provider/models/integrationProvider";
import GlassBackground from "@/shared/components/glassBackground";
import BackButton from "@/shared/components/backButton";

interface SetupFormData {
  IntegrationProviderId: string;
  CredentialsJson: string;
}

const DEFAULT_FORM: SetupFormData = {
  IntegrationProviderId: "",
  CredentialsJson: "",
};

export default function SetupFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { getByProvider, create, update, loading, error } =
    useIntegrationSetups();

  const { getAll, loading: providersLoading } = useIntegrationProviders();

  const hasProviderFromUrl = !!id;

  const [formData, setFormData] = useState<SetupFormData>(DEFAULT_FORM);
  const [providers, setProviders] = useState<IntegrationProvider[]>([]);
  const [providerOptions, setProviderOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedProviderSchema, setSelectedProviderSchema] =
    useState<string>("");
  const [existingSetup, setExistingSetup] =
    useState<IntegrationSetupDto | null>(null);

  // An existing setup means we're editing it; otherwise we're creating one
  // (possibly pre-selecting a provider that doesn't have a setup yet).
  const isEditing = !!existingSetup;

  // Load providers for dropdown
  useEffect(() => {
    const fetchProviders = async () => {
      const page = await getAll({ pageNumber: 1, pageSize: 100 });
      if (page) {
        setProviders(page.items);
        setProviderOptions(
          page.items.map((p) => ({ value: p.id, label: p.name })),
        );
      }
    };
    fetchProviders();
  }, [getAll]);

  // Look up whether the provider from the URL already has a setup.
  // If it doesn't (404), we fall back to creating a new one for that provider.
  useEffect(() => {
    if (hasProviderFromUrl && id) {
      const fetchSetup = async () => {
        const setup = await getByProvider(id);
        if (setup) {
          setExistingSetup(setup);
          setFormData({
            IntegrationProviderId: setup.integrationProviderId,
            CredentialsJson: "", // Backend never returns decrypted credentials
          });
        } else {
          setExistingSetup(null);
          setFormData((prev) => ({ ...prev, IntegrationProviderId: id }));
        }
      };
      fetchSetup();
    }
  }, [id, hasProviderFromUrl, getByProvider]);

  // Load provider schema once we know which provider is selected
  useEffect(() => {
    if (formData.IntegrationProviderId && providers.length > 0) {
      const provider = providers.find(
        (p) => p.id === formData.IntegrationProviderId,
      );
      setSelectedProviderSchema(provider?.developerConfigSchema || "");
    }
  }, [formData.IntegrationProviderId, providers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: IntegrationSetupCreate = {
        IntegrationProviderId: formData.IntegrationProviderId,
        CredentialsJson: formData.CredentialsJson,
      };
      if (existingSetup) {
        await update(existingSetup.id, payload);
      } else {
        await create(payload);
      }
      navigate("/admin/integrations");
    } catch {
      alert("Error al guardar la configuración");
    }
  };

  return (
    <GlassBackground>
      <div className="relative flex-1 overflow-auto p-10">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <BackButton text={"Volver a integraciones"} link={"/admin/integrations"}>
            <h2 className="text-[40px] font-bold tracking-tight text-foreground">
              {isEditing ? "Editar Configuración" : "Nueva Configuración"}
            </h2>
            <p className="text-foreground/50 text-lg font-medium mt-1">
              {isEditing
                ? "Actualiza las credenciales del proveedor."
                : "Configura las credenciales de un proveedor de integración."}
            </p>
          </BackButton>


          {/* Form Card */}
          <div className="bg-white/60 border border-black/5 backdrop-blur-md rounded-4xl p-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.07)]">
            {providersLoading && (
              <div className="flex justify-center py-10">
                <Loader2 className="size-8 animate-spin text-accent" />
              </div>
            )}

            {error && (
              <p className="text-red-500 text-sm mb-6">
                Error: {error.message}
              </p>
            )}

            {!providersLoading && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Provider selector */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Proveedor
                  </h3>
                  <Field
                    label="Proveedor de integración"
                    name="IntegrationProviderId"
                    type="select"
                    value={formData.IntegrationProviderId}
                    onChange={(e) => {
                      const providerId = e.target.value;
                      const provider = providers.find(
                        (p) => p.id === providerId,
                      );
                      setSelectedProviderSchema(
                        provider?.developerConfigSchema || "",
                      );
                      setFormData((prev) => ({
                        ...prev,
                        IntegrationProviderId: providerId,
                        CredentialsJson: "",
                      }));
                    }}
                    placeholder="Selecciona un proveedor..."
                    options={providerOptions}
                    disabled={hasProviderFromUrl}
                    required
                  />
                </div>

                {/* Credentials */}
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Credenciales
                  </h3>
                  {isEditing && (
                    <div className="flex items-start gap-3 p-4 mb-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
                      <ShieldCheck className="size-5 text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-emerald-700">
                          Este proveedor ya tiene credenciales guardadas
                          {existingSetup?.updatedAt &&
                            ` (última actualización: ${new Date(existingSetup.updatedAt).toLocaleDateString()})`}
                          .
                        </p>
                        <p className="text-xs text-emerald-700/70 mt-0.5">
                          Por seguridad no se muestran los valores. Deja los campos
                          vacíos para conservarlas, o escribe nuevos valores para
                          reemplazarlas por completo.
                        </p>
                      </div>
                    </div>
                  )}
                  {selectedProviderSchema ? (
                    <>
                      <p className="text-sm text-foreground/50 mb-3">
                        Completa los campos de configuración para este
                        proveedor:
                      </p>
                      <DynamicJsonForm
                        schema={selectedProviderSchema}
                        value={formData.CredentialsJson}
                        maskPlaceholders={isEditing}
                        onChange={(val) =>
                          setFormData((prev) => ({
                            ...prev,
                            CredentialsJson: val,
                          }))
                        }
                      />
                    </>
                  ) : (
                    <JsonSchemaEditor
                      label="Credenciales (JSON)"
                      name="CredentialsJson"
                      value={formData.CredentialsJson}
                      onChange={(val) =>
                        setFormData((prev) => ({
                          ...prev,
                          CredentialsJson: val,
                        }))
                      }
                      placeholder={
                        isEditing
                          ? '{"clientId": "••••••••", "clientSecret": "••••••••"}'
                          : '{"clientId": "...", "clientSecret": "..."}'
                      }
                      rows={10}
                    />
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-6 border-t border-black/5">
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/admin/integrations")}
                  >
                    Cancelar
                  </Button>
                  <Button
                    submit
                    disabled={loading || providersLoading}
                    className="h-11 px-6 rounded-xl bg-accent hover:bg-accent/90 text-white border-none shadow-[0_0_20px_rgba(12,123,254,0.3)]"
                  >
                    {loading ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Save className="size-4" />
                    )}
                    {isEditing ? "Guardar cambios" : "Crear configuración"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </GlassBackground>
  );
}
